import { create } from 'zustand';
import { api, UploadResult } from '../lib/api';

interface UploadState {
  uploads: Array<{
    id: number;
    name: string;
    progress: number;
    status: 'pending' | 'uploading' | 'completed' | 'failed';
    error?: string;
    url?: string;
    storage_path?: string;
  }>;
  uploadingId: number | null;

  addUpload: (id: number, name: string) => void;
  updateProgress: (id: number, progress: number) => void;
  setUploadStatus: (
    id: number,
    status: 'pending' | 'uploading' | 'completed' | 'failed',
    error?: string
  ) => void;
  startUploading: (id: number) => void;
  completeUpload: (id: number, result: UploadResult) => void;
  failUpload: (id: number, error: string) => void;
  removeUpload: (id: number) => void;
  uploadFile: (file: File) => Promise<UploadResult>;
}

export const useUploadStore = create<UploadState>((set, get) => ({
  uploads: [],
  uploadingId: null,

  addUpload: (id, name) =>
    set((state) => ({
      uploads: [...state.uploads, { id, name, progress: 0, status: 'pending' }],
    })),

  updateProgress: (id, progress) =>
    set((state) => ({
      uploads: state.uploads.map((u) => (u.id === id ? { ...u, progress } : u)),
    })),

  setUploadStatus: (id, status, error) =>
    set((state) => ({
      uploads: state.uploads.map((u) => (u.id === id ? { ...u, status, error } : u)),
    })),

  startUploading: (id) => set({ uploadingId: id }),

  completeUpload: (id, result) =>
    set((state) => ({
      uploads: state.uploads.map((u) =>
        u.id === id
          ? {
              ...u,
              status: 'completed',
              progress: 100,
              url: result.url,
              storage_path: result.storage_path,
            }
          : u
      ),
      uploadingId: state.uploadingId === id ? null : state.uploadingId,
    })),

  failUpload: (id, error) =>
    set((state) => ({
      uploads: state.uploads.map((u) => (u.id === id ? { ...u, status: 'failed', error } : u)),
      uploadingId: state.uploadingId === id ? null : state.uploadingId,
    })),

  removeUpload: (id) =>
    set((state) => ({
      uploads: state.uploads.filter((u) => u.id !== id),
    })),

  uploadFile: async (file: File) => {
    const id = Date.now();
    get().addUpload(id, file.name);
    get().startUploading(id);
    get().updateProgress(id, 10);
    try {
      const result = await api.upload.file(file);
      get().updateProgress(id, 90);
      get().completeUpload(id, result);
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Upload failed';
      get().failUpload(id, message);
      throw error;
    }
  },
}));

/**
 * Upload a file to Bunny Storage via the backend and resolve with the upload
 * result (including the public CDN URL). Unlike `uploadFile` this does not touch
 * the global upload-progress store, so it is safe to use for auxiliary uploads
 * such as product images.
 */
export async function uploadFileToCdn(file: File): Promise<UploadResult> {
  return api.upload.file(file);
}

export function useUploads() {
  const store = useUploadStore();
  return {
    uploads: store.uploads,
    uploadingId: store.uploadingId,
    addUpload: store.addUpload,
    updateProgress: store.updateProgress,
    setUploadStatus: store.setUploadStatus,
    startUploading: store.startUploading,
    completeUpload: store.completeUpload,
    failUpload: store.failUpload,
    removeUpload: store.removeUpload,
    uploadFile: store.uploadFile,
  };
}
